import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  Table,
  Badge,
  Form,
  Row,
  Col,
  Alert,
  ProgressBar,
  Button,
  Spinner,
  Collapse,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  getTeamsMemberQueue,
  getTeamsMemberQueueByTeam,
  TeamsMemberQueueResponse,
  TeamsMemberQueueFilter,
} from "../../services/api";
import { useErrorStore } from "../../errorStore";
import { ArrowClockwise, ChevronDown, ChevronRight } from "react-bootstrap-icons";

export default function TeamsSyncStatus() {
  const { t } = useTranslation("common");
  const [syncData, setSyncData] = useState<TeamsMemberQueueResponse | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<TeamsMemberQueueFilter>({});
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [teamDetails, setTeamDetails] = useState<Map<string, TeamsMemberQueueResponse>>(new Map());
  const [filteredTeams, setFilteredTeams] = useState<any[]>([]);
  const setErrorMessage = useErrorStore((state) => state.setErrorMessage);

  const loadSyncData = async () => {
    try {
      const data = await getTeamsMemberQueue(filter);
      setSyncData(data);
      // Teams filtern basierend auf aktiven Filtern
      applyTeamFilters(data);
    } catch (error: any) {
      setErrorMessage(t("error.loadingFailed"));
    } finally {
      setLoading(false);
    }
  };

  const applyTeamFilters = (data: TeamsMemberQueueResponse) => {
    if (!data || !data.teams) {
      setFilteredTeams([]);
      return;
    }

    let filtered = [...data.teams];

    // Nach Team-Name filtern
    if (filter.team_name) {
      filtered = filtered.filter(team => 
        team.team_name.toLowerCase().includes(filter.team_name!.toLowerCase())
      );
    }

    // Nach Status filtern - hier prüfen wir die Team-Member-Status
    if (filter.status) {
      filtered = filtered.filter(team => {
        const completed = parseInt(team.completed.toString());
        const pending = parseInt(team.pending.toString());
        const failed = parseInt(team.failed.toString());
        const total = parseInt(team.total_members.toString());
        const processing = Math.max(0, total - completed - pending - failed);

        switch (filter.status) {
          case 'completed':
            return completed === total && total > 0;
          case 'pending':
            return pending > 0;
          case 'processing':
            return processing > 0;
          case 'failed':
            return failed > 0;
          default:
            return true;
        }
      });
    }

    setFilteredTeams(filtered);
  };

  useEffect(() => {
    loadSyncData();
    
    if (autoRefresh) {
      const interval = setInterval(loadSyncData, 5000); // Refresh every 5 seconds
      return () => clearInterval(interval);
    }
  }, [filter, autoRefresh]);

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: string } = {
      pending: "warning",
      processing: "info",
      completed: "success",
      failed: "danger",
    };
    return (
      <Badge bg={variants[status] || "secondary"}>
        {t(`teamsSyncStatus.status.${status}`)}
      </Badge>
    );
  };

  const getProgressPercentage = (completed: number, total: number) => {
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const toggleTeamExpansion = async (teamName: string) => {
    const newExpandedTeams = new Set(expandedTeams);
    
    if (expandedTeams.has(teamName)) {
      newExpandedTeams.delete(teamName);
    } else {
      newExpandedTeams.add(teamName);
      
      // Load team details if not already loaded
      if (!teamDetails.has(teamName)) {
        try {
          const details = await getTeamsMemberQueueByTeam(teamName);
          setTeamDetails(prev => new Map(prev).set(teamName, details));
        } catch (error) {
          setErrorMessage(t("error.loadingFailed"));
        }
      }
    }
    
    setExpandedTeams(newExpandedTeams);
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center mt-5">
          <Spinner animation="border" />
        </div>
      </Container>
    );
  }

  if (!syncData) {
    return (
      <Container>
        <Alert variant="danger">{t("error.noData")}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid>
      <h2>{t("teamsSyncStatus.title")}</h2>
      
      <Row className="mb-4">
        <Col md={8}>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{t("teamsSyncStatus.summary")}</h5>
                <div>
                  <Form.Check
                    type="switch"
                    id="auto-refresh"
                    label={t("teamsSyncStatus.autoRefresh")}
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="d-inline-block me-3"
                  />
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={loadSyncData}
                  >
                    <ArrowClockwise /> {t("refresh")}
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col sm={6} lg={3} className="mb-3">
                  <div className="text-center">
                    <h3 className="text-warning">{syncData.summary.pending}</h3>
                    <p className="text-muted mb-0">
                      {t("teamsSyncStatus.pending")}
                    </p>
                  </div>
                </Col>
                <Col sm={6} lg={3} className="mb-3">
                  <div className="text-center">
                    <h3 className="text-info">{syncData.summary.processing}</h3>
                    <p className="text-muted mb-0">
                      {t("teamsSyncStatus.processing")}
                    </p>
                  </div>
                </Col>
                <Col sm={6} lg={3} className="mb-3">
                  <div className="text-center">
                    <h3 className="text-success">{syncData.summary.completed}</h3>
                    <p className="text-muted mb-0">
                      {t("teamsSyncStatus.completed")}
                    </p>
                  </div>
                </Col>
                <Col sm={6} lg={3} className="mb-3">
                  <div className="text-center">
                    <h3 className="text-danger">{syncData.summary.failed}</h3>
                    <p className="text-muted mb-0">
                      {t("teamsSyncStatus.failed")}
                    </p>
                  </div>
                </Col>
              </Row>
              <ProgressBar className="mt-3">
                <ProgressBar
                  variant="success"
                  now={getProgressPercentage(
                    syncData.summary.completed,
                    syncData.summary.total
                  )}
                  label={`${syncData.summary.completed}`}
                />
                <ProgressBar
                  variant="info"
                  now={getProgressPercentage(
                    syncData.summary.processing,
                    syncData.summary.total
                  )}
                />
                <ProgressBar
                  variant="warning"
                  now={getProgressPercentage(
                    syncData.summary.pending,
                    syncData.summary.total
                  )}
                />
                <ProgressBar
                  variant="danger"
                  now={getProgressPercentage(
                    syncData.summary.failed,
                    syncData.summary.total
                  )}
                />
              </ProgressBar>
              <p className="text-muted text-center mt-2">
                {t("teamsSyncStatus.totalEntries", { total: syncData.summary.total })}
              </p>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={4}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">{t("teamsSyncStatus.filter")}</h5>
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>{t("teamsSyncStatus.filterByStatus")}</Form.Label>
                  <Form.Select
                    value={filter.status || ""}
                    onChange={(e) =>
                      setFilter({ ...filter, status: e.target.value || undefined })
                    }
                  >
                    <option value="">{t("all")}</option>
                    <option value="pending">{t("teamsSyncStatus.pending")}</option>
                    <option value="processing">{t("teamsSyncStatus.processing")}</option>
                    <option value="completed">{t("teamsSyncStatus.completed")}</option>
                    <option value="failed">{t("teamsSyncStatus.failed")}</option>
                  </Form.Select>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>{t("teamsSyncStatus.filterByTeam")}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={t("teamsSyncStatus.teamNamePlaceholder")}
                    value={filter.team_name || ""}
                    onChange={(e) =>
                      setFilter({ ...filter, team_name: e.target.value || undefined })
                    }
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>{t("teamsSyncStatus.filterByEmail")}</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder={t("teamsSyncStatus.emailPlaceholder")}
                    value={filter.user_email || ""}
                    onChange={(e) =>
                      setFilter({ ...filter, user_email: e.target.value || undefined })
                    }
                  />
                </Form.Group>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {syncData && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">{t("teamsSyncStatus.teamOverview")}</h5>
          </Card.Header>
          <Card.Body>
            <Table striped responsive>
              <thead>
                <tr>
                  <th style={{ width: "40px" }}></th>
                  <th>{t("teamsSyncStatus.teamName")}</th>
                  <th>{t("teamsSyncStatus.totalMembers")}</th>
                  <th>{t("teamsSyncStatus.progress")}</th>
                  <th>{t("teamsSyncStatus.completed")}</th>
                  <th>{t("teamsSyncStatus.pending")}</th>
                  <th>{t("teamsSyncStatus.failed")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredTeams.length > 0 ? (
                  filteredTeams.map((team, index) => (
                  <React.Fragment key={index}>
                    <tr 
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleTeamExpansion(team.team_name)}
                    >
                      <td>
                        {expandedTeams.has(team.team_name) ? (
                          <ChevronDown />
                        ) : (
                          <ChevronRight />
                        )}
                      </td>
                      <td>
                        <strong>{team.team_name}</strong>
                      </td>
                      <td>{team.total_members}</td>
                      <td>
                        <ProgressBar
                          now={getProgressPercentage(
                            parseInt(team.completed.toString()), 
                            parseInt(team.total_members.toString())
                          )}
                          label={`${getProgressPercentage(
                            parseInt(team.completed.toString()), 
                            parseInt(team.total_members.toString())
                          )}%`}
                          variant="success"
                          style={{ minWidth: "100px" }}
                        />
                      </td>
                      <td>
                        <Badge bg="success">{parseInt(team.completed.toString())}</Badge>
                      </td>
                      <td>
                        <Badge bg="warning">{parseInt(team.pending.toString())}</Badge>
                      </td>
                      <td>
                        <Badge bg="danger">{parseInt(team.failed.toString())}</Badge>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={7} style={{ padding: 0, border: "none" }}>
                        <Collapse in={expandedTeams.has(team.team_name)}>
                          <div>
                            {teamDetails.has(team.team_name) ? (
                              <div style={{ backgroundColor: "#f8f9fa", padding: "15px" }}>
                                <h6 className="mb-3">
                                  {t("teamsSyncStatus.memberDetails")} - {team.team_name}
                                </h6>
                                <Table size="sm" className="mb-0">
                                  <thead>
                                    <tr>
                                      <th>{t("teamsSyncStatus.userEmail")}</th>
                                      <th>{t("teamsSyncStatus.userRole")}</th>
                                      <th>Status</th>
                                      <th>{t("teamsSyncStatus.retryCount")}</th>
                                      <th>{t("teamsSyncStatus.errorMessage")}</th>
                                      <th>{t("teamsSyncStatus.lastUpdate")}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {teamDetails.get(team.team_name)?.details.map((detail) => (
                                      <tr key={detail.id}>
                                        <td>{detail.user_email}</td>
                                        <td>
                                          <Badge 
                                            bg={detail.user_role === "owner" ? "primary" : "secondary"}
                                            className="text-capitalize"
                                          >
                                            {detail.user_role}
                                          </Badge>
                                        </td>
                                        <td>{getStatusBadge(detail.status)}</td>
                                        <td>{detail.retry_count}</td>
                                        <td>
                                          {detail.error_message && (
                                            <small className="text-danger">
                                              {detail.error_message}
                                            </small>
                                          )}
                                        </td>
                                        <td>
                                          <small>
                                            {new Date(detail.updated_at).toLocaleString()}
                                          </small>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            ) : (
                              <div style={{ backgroundColor: "#f8f9fa", padding: "15px", textAlign: "center" }}>
                                <Spinner animation="border" size="sm" />
                                <span className="ms-2">{t("global.loading")}</span>
                              </div>
                            )}
                          </div>
                        </Collapse>
                      </td>
                    </tr>
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      {t("teamsSyncStatus.noTeamsFound")}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {syncData && (
        <Card>
          <Card.Header>
            <h5 className="mb-0">{t("teamsSyncStatus.failedEntries")}</h5>
          </Card.Header>
          <Card.Body>
            <Table striped hover responsive size="sm">
              <thead>
                <tr>
                  <th>{t("teamsSyncStatus.teamName")}</th>
                  <th>{t("teamsSyncStatus.userEmail")}</th>
                  <th>Status</th>
                  <th>{t("teamsSyncStatus.retryCount")}</th>
                  <th>{t("teamsSyncStatus.errorMessage")}</th>
                  <th>{t("teamsSyncStatus.lastUpdate")}</th>
                </tr>
              </thead>
              <tbody>
                {syncData.details.filter((detail) => detail.status === "failed").length > 0 ? (
                  syncData.details
                    .filter((detail) => detail.status === "failed")
                    .map((detail) => (
                      <tr key={detail.id}>
                        <td>{detail.team_name}</td>
                        <td>{detail.user_email}</td>
                        <td>{getStatusBadge(detail.status)}</td>
                        <td>{detail.retry_count}</td>
                        <td>
                          <small className="text-danger">{detail.error_message}</small>
                        </td>
                        <td>
                          <small>{new Date(detail.updated_at).toLocaleString()}</small>
                        </td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      {t("teamsSyncStatus.noFailedEntriesFound")}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}